import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

// Scroll-triggered reveal (styles in index.css). Content is visible by default;
// JS only hides it briefly until it scrolls into view, then transitions it in
// once. No-JS, missing IntersectionObserver or reduced-motion => always visible.
export function Reveal({ children, delay = 0, className, variant = "soft" }) {
  const ref = useRef(null);
  const [state, setState] = useState("idle"); // idle -> armed -> shown

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (
      window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
      !("IntersectionObserver" in window)
    ) {
      setState("shown");
      return;
    }
    setState("armed");
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setState("shown");
            io.disconnect();
            break;
          }
        }
      },
      { threshold: 0.05, rootMargin: "0px 0px -24px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      data-variant={variant}
      className={cn("rv", state === "armed" && "rv-hidden", state === "shown" && "rv-shown", className)}
      style={{ "--rd": `${delay}s` }}
    >
      {children}
    </div>
  );
}

// Alias kept for hero supporting content.
export function FadeIn({ children, delay = 0, className, variant = "soft" }) {
  return (
    <Reveal delay={delay} className={className} variant={variant}>
      {children}
    </Reveal>
  );
}

// Masked line-by-line heading reveal (the signature kinetic moment).
// `lines` accepts strings or { text, className } objects.
export function MaskedHeading({ lines, className, lineClassName, startDelay = 0.1 }) {
  return (
    <span className={cn("block", className)}>
      {lines.map((line, i) => {
        const text = typeof line === "string" ? line : line.text;
        const lineCls = typeof line === "string" ? "" : line.className;
        return (
          <span key={i} className="mask">
            <span
              className={cn("mask-line", lineClassName, lineCls)}
              style={{ "--rd": `${startDelay + i * 0.11}s` }}
            >
              {text}
            </span>
          </span>
        );
      })}
    </span>
  );
}
